import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  Skeleton,
  VStack,
} from '@chakra-ui/react';
import {
  PublicListingPriceHistoryFragment, // Assuming PriceHistoryFragment is the type for priceHistories items
} from '@client/graphql/__generated__/types';
import { Rabbit } from 'lucide-react';
import { FC } from 'react';
import { ContactUsLink } from '~/common/components/ContactUsLink';
import { CardEmptyState } from '../../../../CardEmptyState';
import { useListingPageContext } from '../../ListingPageContext';
import { DataField } from './InfoDataField';
import { ListingPriceHistoryTable } from './ListingPriceHistoryTable';
import {
  OfficeContactFieldMap,
  OfficeFieldMap,
  PropertyFieldMap,
} from './constants';
import { DisplayValue, FieldMap } from './mls-info.types';

// ** Helpers ** //
const renderFields = <TData extends Record<string, unknown>>(
  dataObject: TData | undefined | null,
  fieldMap: FieldMap<TData>
) => {
  if (!dataObject) return null;

  return (Object.keys(fieldMap) as Array<keyof TData>).map((key) => {
    const config = fieldMap[key];
    if (!config) return null;

    const rawValue = dataObject[key];

    const displayValue = config.formatter
      ? config.formatter(rawValue, dataObject)
      : rawValue;

    return (
      <DataField
        key={String(key)}
        label={config.label}
        value={displayValue as DisplayValue}
      />
    );
  });
};

// ** Main Component ** //
export const MlsInfo: FC = () => {
  const { listing, isLoadingReport, isPrint } = useListingPageContext();

  const mlsListingData = listing?.mlsListing;
  const priceHistories = listing?.priceHistories as
    | PublicListingPriceHistoryFragment[]
    | undefined;

  const headingProps = {
    mt: isPrint ? 2 : { base: 0, md: 2 },
    mb: isPrint ? 4 : { base: 2, md: 4 },
    size: 'xxs',
  };

  const gridProps = {
    columns: isPrint ? 3 : { base: 1, sm: 2, md: 3 },
    spacingX: isPrint ? 2 : 6,
    spacingY: 0,
  };

  if (isLoadingReport) {
    return (
      <VStack alignItems="stretch" px={{ base: 2, md: 4 }} spacing={8}>
        {/* Property Skeletons */}
        <Skeleton height="30px" mb={2} width="200px" />
        <SimpleGrid {...gridProps} spacingY={4}>
          {[...Array<number>(6)].map((_, i) => (
            <Skeleton key={`prop-skel-${i}`} height="40px" />
          ))}
        </SimpleGrid>
        {/* Office Skeletons */}
        <Skeleton height="30px" mb={2} mt={4} width="200px" />
        <SimpleGrid {...gridProps}>
          {[...Array<number>(3)].map((_, i) => (
            <Skeleton key={`office-skel-${i}`} height="40px" />
          ))}
        </SimpleGrid>
        {/* Price History Skeletons */}
        <Skeleton height="30px" mb={2} mt={4} width="200px" />{' '}
        {/* Heading Skeleton */}
        <Box borderColor="whiteAlpha.200">
          {/* Skeleton for Thead */}
          <Flex
            borderBottomWidth="1px"
            borderColor="whiteAlpha.200"
            justifyContent="space-between"
            py={4}
          >
            {[...Array<number>(3)].map((_, j) => (
              <Skeleton
                key={`ph-head-skel-${j}`}
                height="20px"
                mr={j < 5 ? 2 : 0}
                width="80px"
              />
            ))}
          </Flex>
          {/* Skeleton for Tbody rows */}
          {[...Array<number>(2)].map((_, i) => (
            <Flex
              key={`ph-row-skel-${i}`}
              borderBottomWidth={i < 1 ? '1px' : '0px'}
              borderColor="whiteAlpha.200"
              justifyContent="space-between"
              py={4}
            >
              {[...Array<number>(3)].map((_, j) => (
                <Skeleton
                  key={`ph-cell-skel-${i}-${j}`}
                  height="20px"
                  mr={j < 5 ? 2 : 0}
                  width="80px"
                />
              ))}
            </Flex>
          ))}
        </Box>
      </VStack>
    );
  }

  const property = mlsListingData?.property;
  const office = mlsListingData?.office;
  const officeContact = office?.contact;

  const hasPropertyData = !!property;
  const hasOfficeData = !!office;
  const hasPriceHistoryData = !!priceHistories && priceHistories.length > 0;

  if (!hasPropertyData && !hasOfficeData && !hasPriceHistoryData) {
    return (
      <CardEmptyState
        description={
          <>
            MLS data is currently not available for this listing. <br />
            We're working on it! <ContactUsLink /> if you'd like to help us out.
          </>
        }
        icon={Rabbit}
        title="Limited MLS Data"
      />
    );
  }

  return (
    <VStack
      alignItems="stretch"
      px={{ base: 2, md: 4 }}
      spacing={isPrint ? 2 : 8}
    >
      {/* Property Details Section */}
      {hasPropertyData && (
        <Box>
          <Heading {...headingProps}>Property Details</Heading>
          <SimpleGrid {...gridProps}>
            {renderFields(property, PropertyFieldMap)}
          </SimpleGrid>
        </Box>
      )}

      {/* Office Information Section */}
      {hasOfficeData && (
        <Box>
          <Heading {...headingProps}>Office Information</Heading>
          <SimpleGrid {...gridProps}>
            {renderFields(office, OfficeFieldMap)}
            {officeContact &&
              renderFields(officeContact, OfficeContactFieldMap)}
          </SimpleGrid>
        </Box>
      )}

      {/* Price History Section */}
      {hasPriceHistoryData && (
        <Box>
          <Heading {...headingProps}>Price History</Heading>
          <ListingPriceHistoryTable priceHistories={priceHistories} />
        </Box>
      )}
    </VStack>
  );
};
